import { useEffect, useState } from "react";
import { FileText, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { openAttachmentViewer } from "@/lib/attachmentViewer";

const oggi = () => new Date().toISOString().slice(0, 10);
const vuoto = () => ({ data: oggi(), km: "", descrizione: "" });

export default function ManutenzioniAttrezzaturaDialog({ attrezzatura, onClose }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(vuoto());
  const [editing, setEditing] = useState(null);
  const [file, setFile] = useState(null);

  const load = async () => {
    if (!attrezzatura?.id) return;
    try {
      const response = await api.get(`/attrezzi-catalogo/${attrezzatura.id}/manutenzioni`);
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, [attrezzatura?.id]);

  const reset = () => { setEditing(null); setForm(vuoto()); setFile(null); };

  const save = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        data: form.data,
        km: form.km === "" ? null : Number(String(form.km).replace(",", ".")),
        descrizione: form.descrizione.trim(),
      };
      const response = editing
        ? await api.put(`/attrezzi-catalogo/${attrezzatura.id}/manutenzioni/${editing.id}`, payload)
        : await api.post(`/attrezzi-catalogo/${attrezzatura.id}/manutenzioni`, payload);
      const id = editing?.id || response.data.id;
      if (file) {
        const data = new FormData();
        data.append("file", file);
        await api.post(`/attrezzi-catalogo/${attrezzatura.id}/manutenzioni/${id}/file`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Manutenzione salvata");
      reset();
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const edit = (item) => {
    setEditing(item);
    setForm({ data: item.data || oggi(), km: item.km ?? "", descrizione: item.descrizione || "" });
    setFile(null);
  };

  const remove = async (item) => {
    if (!window.confirm("Eliminare questa manutenzione e il relativo allegato?")) return;
    try {
      await api.delete(`/attrezzi-catalogo/${attrezzatura.id}/manutenzioni/${item.id}`);
      if (editing?.id === item.id) reset();
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  return <Dialog open={Boolean(attrezzatura)} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Manutenzioni — {attrezzatura?.nome}</DialogTitle>
        <DialogDescription>Registra data, chilometri o ore macchina, intervento e allegato.</DialogDescription>
      </DialogHeader>
      <form onSubmit={save} className="rounded-xl border bg-muted/20 p-4 space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label>Data *</Label><Input type="date" value={form.data} onChange={(e) => setForm({...form, data:e.target.value})} required /></div>
          <div><Label>Km / ore macchina</Label><Input type="number" min="0" step="0.01" value={form.km} onChange={(e) => setForm({...form, km:e.target.value})} /></div>
          <div className="sm:col-span-2"><Label>Descrizione *</Label><Textarea value={form.descrizione} onChange={(e) => setForm({...form, descrizione:e.target.value})} required /></div>
          <div className="sm:col-span-2"><Label>Allegato</Label><Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.gif" onChange={(e) => setFile(e.target.files?.[0] || null)} /></div>
        </div>
        <div className="flex justify-end gap-2">
          {editing && <Button type="button" variant="outline" onClick={reset}>Annulla modifica</Button>}
          <Button type="submit"><Plus className="mr-2 h-4 w-4" />{editing ? "Salva modifiche" : "Aggiungi manutenzione"}</Button>
        </div>
      </form>
      <div className="rounded-xl border overflow-hidden">
        <div className="max-h-[360px] overflow-auto">
          <table className="w-full min-w-[700px] text-sm">
            <thead className="sticky top-0 bg-muted"><tr className="text-left"><th className="p-3">Data</th><th className="p-3">Km / ore</th><th className="p-3">Descrizione</th><th className="p-3">Allegato</th><th className="p-3"></th></tr></thead>
            <tbody>{items.length === 0 ? <tr><td colSpan="5" className="p-8 text-center text-muted-foreground">Nessuna manutenzione registrata</td></tr> : items.map((item) => <tr key={item.id} className="border-t align-top">
              <td className="p-3 whitespace-nowrap">{item.data?.split("-").reverse().join("/")}</td>
              <td className="p-3">{item.km ?? "-"}</td>
              <td className="p-3 whitespace-pre-wrap break-words">{item.descrizione}</td>
              <td className="p-3">{item.file_nome ? <button type="button" className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => openAttachmentViewer(`${api.defaults.baseURL}/attrezzi-catalogo/${attrezzatura.id}/manutenzioni/${item.id}/file`, item.file_nome)}><FileText className="h-4 w-4" />{item.file_nome}</button> : "-"}</td>
              <td className="p-3 whitespace-nowrap"><Button type="button" variant="ghost" size="icon" onClick={() => edit(item)}><Pencil className="h-4 w-4" /></Button><Button type="button" variant="ghost" size="icon" onClick={() => remove(item)}><Trash2 className="h-4 w-4 text-destructive" /></Button></td>
            </tr>)}</tbody>
          </table>
        </div>
      </div>
      <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Chiudi</Button></DialogFooter>
    </DialogContent>
  </Dialog>;
}
